import * as Dialog from '@radix-ui/react-dialog'
import { Suspense } from 'react'
import { Spinner } from '../../components/spinner.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { usePostAddRepository, useSuspenseGetIntegrations } from '../../utils/api-client/api-hooks.ts'
import type { GitHubIntegration } from '../../utils/api-client/api-schemas'
import { getEnvironmentData } from '../../utils/hooks/get-environment-data.ts'
import { AddRepositoryForm, useAddRepositoryForm } from './add-repository-form'
import * as styles from './add-repository-modal.css'

type AddRepositoryModalProps = {
  onClose: () => unknown
}

export const AddRepositoryModal: React.FC<AddRepositoryModalProps> = ({ onClose }) => {
  return (
    <Dialog.Root open={true}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.dialogOverlay} />
        <Dialog.Content
          className={styles.dialogContent}
          aria-describedby={undefined}
          onEscapeKeyDown={onClose}
          tabIndex={-1}
        >
          <Dialog.Title className={styles.title}>New repository</Dialog.Title>
          <Suspense
            fallback={
              <div className={styles.spinnerContainer}>
                <Spinner size="64" variant="cool" />
              </div>
            }
          >
            <AddRepositoryFormContainer onClose={onClose} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const AddRepositoryFormContainer: React.FC<{ onClose: () => unknown }> = ({ onClose }) => {
  const environmentData = getEnvironmentData()
  const { addToast } = useToastService()
  const { data: paginatedIntegrations } = useSuspenseGetIntegrations()

  const integrations = paginatedIntegrations._embedded?.items ?? []

  const showMutationError = useShowMutationError({ prefix: 'Repository connection failed' })
  const addRepositoryMutation = usePostAddRepository({
    onSuccess: () => {
      addToast({ message: <>Repository was successfully connected.</>, variant: 'success' })
      onClose()
    },
    onError: showMutationError,
  })

  const githubIntegration = integrations.find(
    (integration): integration is GitHubIntegration => integration.type === 'github'
  )
  const azureIntegration = integrations.find(integration => integration.type === 'azure')
  const gitlabIntegration = integrations.find(integration => integration.type === 'gitlab')
  const bitbucketIntegration = integrations.find(integration => integration.type === 'bitbucket')

  const githubAppName = githubIntegration?.app_name
  const isAzureIntegrationEnabled = !!azureIntegration
  const isGitlabIntegrationEnabled = !!gitlabIntegration
  const isBitbucketIntegrationEnabled = !!bitbucketIntegration

  const form = useAddRepositoryForm({
    selectedConnectionType: githubAppName
      ? 'GitHubApp'
      : isAzureIntegrationEnabled || isGitlabIntegrationEnabled || isBitbucketIntegrationEnabled
        ? 'Integration'
        : 'GenericGit',
    selectedIntegration: isAzureIntegrationEnabled
      ? 'azureDevOps'
      : isGitlabIntegrationEnabled
        ? 'gitlab'
        : 'bitbucket',
  })

  return (
    <form.AppForm>
      <AddRepositoryForm
        environmentName={environmentData.name}
        onClose={onClose}
        githubAppName={githubAppName}
        isAzureIntegrationEnabled={isAzureIntegrationEnabled}
        isGitlabIntegrationEnabled={isGitlabIntegrationEnabled}
        isBitbucketIntegrationEnabled={isBitbucketIntegrationEnabled}
        addRepository={(params, options) => addRepositoryMutation.mutate(params, options)}
      />
    </form.AppForm>
  )
}
